import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Field, getFormValues, getFormAsyncErrors } from "redux-form";

import { Button, Modal, ReduxControls, EmptyItem } from "rhombus-react";

import { MAX_VERSION_GEN_CREATIVES } from "utils/constants";
import { AI_GENERATE_VERSIONS_FORM, MODES } from "./constants";
import { useVariableBounds } from "./utils";
import { getVersionGenSelectedPreview } from "utils/Flags";
import { addNotification } from "reducers/Notifications";

import Previews from "./Previews";
import PromptPanel from "./PromptPanel";
import TooManyCreatives from "./TooManyCreatives";

import { clearRefinements } from "reducers/Refinements";
import {
	generateAssets,
	getUniqueVersionNames,
	loadVersionsByName
} from "reducers/VersionGeneration";

export default function CurrentVersionsBlade(props){
	const {
		libraryId,
		onClose,
		onNext,
		valid,
		mode,
		anyTouched,
	} = props;
	const asyncErrors = useSelector(getFormAsyncErrors(AI_GENERATE_VERSIONS_FORM));
	const dispatch = useDispatch();

	const isAIDevEnabled = getVersionGenSelectedPreview()
		, devClass = isAIDevEnabled ? "ai-dev-mode" : ""
		;

	const {
		isVariablesLoaded,
		isCreativesLoaded,
		creatives,
		variables = [],
		uniqueVersionNames = {},
		variations = [],
	} = useSelector(state => state.versionGeneration || {});

	const {
		items: uniqueVersionNamesItems = [],
		page: uniqueVersionPage,
		totalPages: uniqueVersionTotalPages,
		isLoading: uniqueVersionIsLoading,
	} = uniqueVersionNames;

	const formValues = useSelector(getFormValues(AI_GENERATE_VERSIONS_FORM));
	const conceptName = useSelector(state => state.conceptDetails.data?.conceptName || "");
	const { getTextWithBounds, getImageWithDimensions } = useVariableBounds();

	const brandKits = formValues["brand-kits"];
	const {
		prompt,
		variables: selectedVariables,
		sourceVersion,
		audiences,
		products,
	} = formValues;

	const title = (mode === MODES.CONCEPT) ? `Generate Versions for Concept - ${conceptName}` : "Generate Versions";

	const isOverLimit = creatives.length > MAX_VERSION_GEN_CREATIVES;
	const isCreativesEmpty = isCreativesLoaded && !creatives.length;

	const versionNameOptions = uniqueVersionNamesItems.map(value => ({ value: value.name, label: value.name }));

	const disableSwitchToGenerated = !variations.length || asyncErrors?.versionNamePrefix;

	const creativeList = creatives.map(creative => ({ id: creative.id }));

	const loadMoreSourceVersions = () => {
		if (uniqueVersionPage >= uniqueVersionTotalPages || uniqueVersionIsLoading) {
			return;
		}

		dispatch(getUniqueVersionNames(libraryId, creativeList, { inAllCreatives: true, page: uniqueVersionPage + 1 }));
	};

	useEffect(() => {
		if(!creatives?.length || isOverLimit){ return; }

		dispatch(getUniqueVersionNames(libraryId, creativeList, { inAllCreatives: true, page: uniqueVersionPage }));
	}, [libraryId, creatives, isOverLimit]);

	useEffect(() => {
		dispatch(loadVersionsByName(libraryId, sourceVersion));
	}, [sourceVersion]);

	async function generateVersions() {
		// Manually call asyncValidate.
		// This is needed cause user may immediatly click Generate versions button
		// which will trigger submit before blur event and validation happend - racing.
		// Which will cause wasted JENAI calls.
		props.asyncValidate("versionNamePrefix", formValues.versionNamePrefix, "blur").then((error) => {
			if(error){
				// To trigger error message and error styling around the field we need to call touch
				props.touch("versionNamePrefix");
				return;
			}

			// Only proceed when async validation was successfull this will save us wasted JENAI calls
			const texts = variables
				.filter(variable => selectedVariables[variable.name] && variable.type === "Text")
				.map(getTextWithBounds);
			const images = variables
				.filter(variable => selectedVariables[variable.name] && variable.type === "Image")
				.map(getImageWithDimensions);

			const data = {
				prompt,
				texts,
				images,
				// Conditionally add metadata to request body
				brandKits: brandKits ? [{ id: brandKits }] : [],
				audiences: audiences ? [{ id: audiences }] : [],
				products: products ? [{ id: products }] : [],
			};

			// Show notification to inform user that request is being processed and may take a while.
			// Since this is combined operation of bulk cropping then applying crops to each variation
			// and passing binaries to iframe, it may take a while. And feedback to user is needed.
			dispatch(addNotification({
				id: Math.random(),
				messageHeader: "Processing Request",
				message: "Version generation and cropping may take a while. Please be patient.",
				dismissIn: 10000,
			}));
			dispatch(clearRefinements());
			dispatch(generateAssets(libraryId, data));
			onNext();
		});
	}

	function goToGeneratedVersions(){
		if(!disableSwitchToGenerated){
			onNext();
		}
	}

	function renderContent(){
		let mainContent = (
			<>
				<Field
					name="sourceVersion"
					component={ReduxControls.Select}
					className="source-version-select cypress-gen-ai-source-version-select"
					options={versionNameOptions}
					isClearable={false}
					normalize={value => value?.value}
					format={value => ({ label: value, value })}
					onMenuScrollToBottom={loadMoreSourceVersions}
				/>
				<Previews
					isLoading = {!isVariablesLoaded}
					creatives = {creatives}
					sourceVersion={sourceVersion}
					showFooter={true}
				/>
			</>
		);
		if(isOverLimit){
			mainContent = <TooManyCreatives onClose={onClose} />;
		}

		if(isCreativesEmpty) {
			mainContent = <EmptyItem title="No Creatives Loaded" />;
		}

		return (
			<div className="blade-content-container">
				<div className="main-content">
					<div className="blade-toolbar">
						<div className="active cypress-gen-ai-current-versions-tab">Current Versions</div>
						<div className={`cypress-gen-ai-generated-versions-tab ${disableSwitchToGenerated && "disabled"}`} onClick={goToGeneratedVersions}>
							Generated{variations.length ? ` ${variations.length}` : ""} Versions
						</div>
					</div>
					{mainContent}
				</div>
				<div className="panel">
					<PromptPanel
						libraryId={libraryId}
						isDisabled={isOverLimit || isCreativesEmpty}
						anyTouched={anyTouched} />
				</div>
			</div>
		);
	}

	function renderFooter(){
		return (
			<>
				<Button
					theme="default"
					className="cypress-gen-ai-cancel-button"
					onClick={onClose}>
					Cancel
				</Button>
				<Button
					icon="fas fa-wand-magic-sparkles"
					onClick={generateVersions}
					className="cypress-gen-ai-submit-button"
					disabled={!isVariablesLoaded || !variables.length || !valid || asyncErrors?.versionNamePrefix}
				>
					Generate Versions
				</Button>
			</>
		);
	}

	const bladeProps = {
		title,
		className: `generate-versions-concept-blade ${devClass}`,
		renderContent,
		renderFooter,
		allowFullScreen: false,
		onClose,
	};

	return <Modal {...bladeProps} />;
}

CurrentVersionsBlade.displayName = "CurrentVersionsBlade";
